import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * useStripeConnect — encapsulates Stripe Connect status fetching and the
 * three side-effecting actions (start onboarding / open dashboard / refresh).
 *
 * The component renders pure UI on top of this — keeps StripeConnectCard
 * under 100 lines.
 */
export function useStripeConnect() {
  const [status, setStatus] = useState(null);   // null = loading
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/payments/connect/status`, { withCredentials: true });
      setStatus(data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load Stripe status');
      setStatus({ has_account: false, onboarding_complete: false });
    }
  }, []);

  useEffect(() => {
    loadStatus();
    // Re-poll if the user just returned from Stripe-hosted onboarding.
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe_connect') === 'return') {
      params.delete('stripe_connect');
      const qs = params.toString();
      window.history.replaceState({}, document.title, window.location.pathname + (qs ? '?' + qs : ''));
      setTimeout(loadStatus, 1500);
      setTimeout(loadStatus, 4500);
    }
  }, [loadStatus]);

  const startOrResumeOnboarding = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const path = status?.has_account ? '/api/payments/connect/refresh' : '/api/payments/connect/onboard';
      const { data } = await axios.post(
        `${API}${path}`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = data.url;
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not start Stripe onboarding');
      setBusy(false);
    }
  }, [status?.has_account]);

  const openDashboard = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/payments/connect/dashboard`, {}, { withCredentials: true });
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not open Stripe dashboard');
    } finally {
      setBusy(false);
    }
  }, []);

  return { status, error, busy, loadStatus, startOrResumeOnboarding, openDashboard };
}
